"use client";

import { Copy } from "iconsax-reactjs";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  encodeAbiParameters,
  formatUnits,
  keccak256,
  parseAbiParameters,
  parseUnits,
} from "viem";
import { useAccount, useReadContracts } from "wagmi";
import { WalletAddress } from "@/components/core/WalletAddress";
import { Button } from "@/components/ui/button";
import ContractAddress from "@/contract/addresses";

const RPC_URL = process.env.NEXT_PUBLIC_RPC_URL ?? "http://localhost:8545";

const NATIVE_FUND_AMOUNT = parseUnits("10000", 18);
const USDT_FUND_AMOUNT = parseUnits("100000", 6);
const UVM_FUND_AMOUNT = parseUnits("100000", 18);

// Both USDT and UVM store balances in a standard OpenZeppelin-shaped
// `mapping(address => uint256)` at storage slot 0 on this fork -- verified
// empirically against the actual deployed bytecode, not assumed.
const BALANCE_MAPPING_SLOT = 0n;

const ERC20_BALANCE_OF = [
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
] as const;

async function rpcCall(method: string, params: unknown[]): Promise<unknown> {
  const res = await fetch(RPC_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", method, params, id: 1 }),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message ?? "RPC error");
  return json.result;
}

function balanceSlot(address: `0x${string}`): `0x${string}` {
  return keccak256(
    encodeAbiParameters(parseAbiParameters("address, uint256"), [
      address,
      BALANCE_MAPPING_SLOT,
    ]),
  );
}

async function setErc20Balance(
  token: `0x${string}`,
  holder: `0x${string}`,
  amount: bigint,
): Promise<void> {
  const slot = balanceSlot(holder);
  const value = `0x${amount.toString(16).padStart(64, "0")}` as const;
  await rpcCall("anvil_setStorageAt", [token, slot, value]);
}

async function getNativeBalance(address: `0x${string}`): Promise<bigint> {
  const result = (await rpcCall("eth_getBalance", [
    address,
    "latest",
  ])) as string;
  return BigInt(result);
}

async function copyToClipboard(label: string, value: string) {
  await navigator.clipboard.writeText(value);
  toast.success(`${label} address copied.`);
}

/**
 * Dev-only tool for the local anvil fork: real V1 wallets migrated onto
 * Gateway V2 carry their real on-chain history, but attendees testing with
 * their own wallet at the office almost certainly hold none of the fork's
 * play-money UVM/USDT locally, and have no POL for gas either -- checkout
 * is unusable without this. Writes storage/balance directly
 * (anvil_setBalance / anvil_setStorageAt), not a real transfer, so it costs
 * no gas and needs no funded source wallet. The trigger that renders this
 * is itself gated to NET_MODE === "FORK" only (see TopNavLogin) -- no
 * user-role check, unlike ImpersonationPanel, since "is this the local
 * fork" is the only condition that matters here.
 *
 * Native balance is read via a raw eth_getBalance call rather than wagmi's
 * useBalance: after anvil_setBalance the write is verified with the same
 * raw call before the success toast fires, so "funded" can never be shown
 * while the underlying node still reports the old balance.
 */
export const FundWalletPanel: React.FC = () => {
  const { address, isConnected } = useAccount();
  const [nativeBalance, setNativeBalance] = useState<bigint | undefined>();

  const tokens = useReadContracts({
    contracts: address
      ? [
          {
            address: ContractAddress.USDT,
            abi: ERC20_BALANCE_OF,
            functionName: "balanceOf",
            args: [address],
          },
          {
            address: ContractAddress.UVM,
            abi: ERC20_BALANCE_OF,
            functionName: "balanceOf",
            args: [address],
          },
        ]
      : [],
    query: { enabled: !!address },
  });

  const refreshNative = useCallback(() => {
    if (address) getNativeBalance(address).then(setNativeBalance);
  }, [address]);

  useEffect(() => {
    refreshNative();
  }, [refreshNative]);

  const handleFund = async () => {
    if (!address) return;
    try {
      await rpcCall("anvil_setBalance", [
        address,
        `0x${NATIVE_FUND_AMOUNT.toString(16)}`,
      ]);
      // Verify against the node directly rather than trusting the RPC
      // returning no error -- a "funded" toast must never lie about the
      // actual on-chain balance a transaction will pay gas from.
      const confirmed = await getNativeBalance(address);
      if (confirmed < NATIVE_FUND_AMOUNT) {
        throw new Error(
          `anvil_setBalance did not take effect (node reports ${confirmed} wei).`,
        );
      }
      setNativeBalance(confirmed);

      await setErc20Balance(ContractAddress.USDT, address, USDT_FUND_AMOUNT);
      await setErc20Balance(ContractAddress.UVM, address, UVM_FUND_AMOUNT);
      toast.success("Wallet funded — 10,000 POL, 100,000 USDT, 100,000 UVM.");
      tokens.refetch();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to fund wallet.",
      );
    }
  };

  const CopyAddress = ({
    label,
    address: tokenAddress,
  }: {
    label: string;
    address: string;
  }) => (
    <button
      type="button"
      aria-label={`Copy ${label} contract address`}
      onClick={() => copyToClipboard(label, tokenAddress)}
      className="text-text-secondary hover:text-text-primary transition-colors"
    >
      <Copy size={14} />
    </button>
  );

  return (
    <div className="w-full rounded-lg border border-stroke-light bg-bg-secondary p-4 space-y-3">
      <h3 className="text-sm font-semibold text-text-primary">
        Fund wallet (fork only)
      </h3>

      {!isConnected || !address ? (
        <p className="text-xs text-text-secondary">
          Connect a wallet to fund it on the local fork.
        </p>
      ) : (
        <>
          <div
            dir="ltr"
            className="flex items-center gap-2 text-xs text-text-secondary"
          >
            <span>Wallet:</span>
            <WalletAddress address={address} />
          </div>

          <div
            dir="ltr"
            className="rounded-md bg-bg-primary p-2 space-y-1.5 text-xs"
          >
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">
                {/* Native currency has no ERC-20 contract address to add in
                    MetaMask -- it's available automatically once the fork
                    network itself is added there. */}
                POL
              </span>
              <span>
                {nativeBalance !== undefined
                  ? formatUnits(nativeBalance, 18)
                  : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-text-secondary">
                USDT
                <CopyAddress label="USDT" address={ContractAddress.USDT} />
              </span>
              <span>
                {tokens.data?.[0]?.result !== undefined
                  ? formatUnits(tokens.data[0].result as bigint, 6)
                  : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-text-secondary">
                UVM
                <CopyAddress label="UVM" address={ContractAddress.UVM} />
              </span>
              <span>
                {tokens.data?.[1]?.result !== undefined
                  ? formatUnits(tokens.data[1].result as bigint, 18)
                  : "—"}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="solid"
            colors="secondary"
            className="w-full"
            onClick={handleFund}
          >
            Fund
          </Button>
        </>
      )}
    </div>
  );
};
